/**
 * AdminYoloDataset — visibility into the proprietary ML dataset being
 * silently gathered by the Shot Analyzer (YOLO Data Capture module).
 *
 * Sections
 * ────────
 * 1. KPI strip — total shots, total frames, consenting users, makes/misses.
 * 2. Class distribution — bar chart of YOLO classes (ball, player, hoop, backboard).
 * 3. Growth chart — daily shot/frame ingestion over last 12 weeks.
 * 4. Manifest table — per-shot prefixes the admin can sync with an S3-compatible client.
 *
 * Admin only. Route is enforced upstream by `<Protected roles={['admin']}>`.
 */
import React from 'react';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Database, Activity, Image as ImageIcon, Users, Download, RefreshCw, FileCode2 } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar,
} from 'recharts';

const fmt = (n) => Number(n || 0).toLocaleString('es-ES');

function KpiCard({ label, value, accent = 'text-gold', icon: Icon, testid }) {
  return (
    <div className="rounded-xl border border-gold/20 bg-navy-deep p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-slate-400">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-gold/70" />}
      </div>
      <div className={`mt-2 font-h text-3xl tabular-nums ${accent}`}>{value}</div>
    </div>
  );
}

export default function AdminYoloDataset() {
  const [stats, setStats] = React.useState(null);
  const [growth, setGrowth] = React.useState(null);
  const [manifest, setManifest] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const [s, g, m] = await Promise.all([
        api.get('/yolo/admin/stats'),
        api.get('/yolo/admin/growth', { params: { weeks: 12 } }),
        api.get('/yolo/admin/manifest'),
      ]);
      setStats(s.data);
      setGrowth(g.data);
      setManifest(m.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudieron cargar las estadísticas YOLO.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const downloadManifest = () => {
    if (!manifest) return;
    const blob = new Blob([JSON.stringify(manifest, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `yolo-manifest-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success('Manifest descargado. Úsalo con tu cliente S3 favorito.');
  };

  const downloadDatasetYaml = async () => {
    try {
      const r = await api.get('/yolo/admin/dataset-yaml', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'dataset.yaml';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('dataset.yaml listo. Úsalo con: yolo train data=dataset.yaml model=yolov8n.pt');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo generar dataset.yaml');
    }
  };

  const classData = React.useMemo(() => {
    if (!stats?.class_counts) return [];
    return Object.entries(stats.class_counts).map(([name, count]) => ({ name, count }));
  }, [stats]);

  const resultData = React.useMemo(() => {
    if (!stats?.by_result) return [];
    return Object.entries(stats.by_result).map(([name, n]) => ({ name, n }));
  }, [stats]);

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-10 py-6 max-w-7xl mx-auto" data-testid="admin-yolo-dataset">
        <BackToDashboard />

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="font-h text-3xl sm:text-4xl uppercase tracking-tight flex items-center gap-3">
              <Database className="w-8 h-8 text-gold" /> YOLO Dataset
            </h1>
            <p className="text-sm text-slate-400 mt-1 max-w-xl">
              Estadísticas en tiempo real del dataset propietario recolectado por el Shot Analyzer.
              Cada disparo aporta frames con bounding boxes para entrenar un modelo YOLOv8 a medida.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={refresh}
              data-testid="yolo-refresh-btn"
              className="inline-flex items-center gap-2 rounded-lg border border-gold/30 bg-navy-deep px-3 py-2 text-sm hover:bg-gold/10"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
            </button>
            <button
              onClick={downloadManifest}
              disabled={!manifest}
              data-testid="yolo-download-manifest-btn"
              className="inline-flex items-center gap-2 rounded-lg border border-gold bg-gold/20 px-3 py-2 text-sm font-h uppercase tracking-wider text-gold hover:bg-gold/30 disabled:opacity-40"
            >
              <Download className="w-4 h-4" /> Manifest JSON
            </button>
            <button
              onClick={downloadDatasetYaml}
              data-testid="yolo-download-yaml-btn"
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-400 bg-emerald-400/15 px-3 py-2 text-sm font-h uppercase tracking-wider text-emerald-300 hover:bg-emerald-400/25"
            >
              <FileCode2 className="w-4 h-4" /> dataset.yaml
            </button>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          <KpiCard label="Disparos" value={fmt(stats?.total_shots)} icon={Activity} testid="kpi-shots" />
          <KpiCard label="Frames" value={fmt(stats?.total_frames)} icon={ImageIcon} testid="kpi-frames" />
          <KpiCard label="Usuarios opt-in" value={fmt(stats?.consenting_users)} icon={Users} testid="kpi-consent" />
          <KpiCard label="Aciertos" value={fmt(stats?.by_result?.make)} accent="text-emerald-400" testid="kpi-makes" />
          <KpiCard label="Fallos" value={fmt(stats?.by_result?.miss)} accent="text-rose-400" testid="kpi-misses" />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="rounded-xl border border-gold/20 bg-navy-deep p-5" data-testid="yolo-class-chart">
            <h2 className="font-h text-lg uppercase tracking-tight mb-4">Distribución de clases</h2>
            {classData.length === 0 ? (
              <div className="text-sm text-slate-500 py-8 text-center">Sin datos todavía.</div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={classData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: '1px solid rgba(212,175,55,0.4)', borderRadius: 8 }}
                    labelStyle={{ color: '#d4af37' }}
                  />
                  <Bar dataKey="count" fill="#d4af37" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="rounded-xl border border-gold/20 bg-navy-deep p-5" data-testid="yolo-result-chart">
            <h2 className="font-h text-lg uppercase tracking-tight mb-4">Resultado de los disparos</h2>
            {resultData.every(r => r.n === 0) ? (
              <div className="text-sm text-slate-500 py-8 text-center">Sin datos todavía.</div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={resultData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                  <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip
                    contentStyle={{ background: '#0f172a', border: '1px solid rgba(212,175,55,0.4)', borderRadius: 8 }}
                    labelStyle={{ color: '#d4af37' }}
                  />
                  <Bar dataKey="n" fill="#34d399" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Growth chart */}
        <div className="rounded-xl border border-gold/20 bg-navy-deep p-5 mb-8" data-testid="yolo-growth-chart">
          <h2 className="font-h text-lg uppercase tracking-tight mb-4">Crecimiento diario (12 semanas)</h2>
          {!growth?.series?.length ? (
            <div className="text-sm text-slate-500 py-8 text-center">Aún no hay capturas en este rango.</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={growth.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                <XAxis dataKey="day" stroke="#94a3b8" fontSize={11} />
                <YAxis stroke="#94a3b8" fontSize={11} />
                <Tooltip
                  contentStyle={{ background: '#0f172a', border: '1px solid rgba(212,175,55,0.4)', borderRadius: 8 }}
                  labelStyle={{ color: '#d4af37' }}
                />
                <Line type="monotone" dataKey="shots" stroke="#d4af37" strokeWidth={2} dot={false} name="Disparos" />
                <Line type="monotone" dataKey="frames" stroke="#60a5fa" strokeWidth={2} dot={false} name="Frames" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Manifest table */}
        <div className="rounded-xl border border-gold/20 bg-navy-deep overflow-hidden" data-testid="yolo-manifest-table">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gold/10">
            <h2 className="font-h text-lg uppercase tracking-tight">Manifest ({fmt(manifest?.total_shots)})</h2>
            <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-slate-400">
              {stats?.storage_prefix || ''}
            </span>
          </div>
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-sm">
              <thead className="bg-navy text-slate-400 text-[10px] font-mono uppercase tracking-[0.2em]">
                <tr>
                  <th className="text-left px-5 py-2">Shot ID</th>
                  <th className="text-left px-5 py-2">Prefix</th>
                  <th className="text-right px-5 py-2">Frames</th>
                </tr>
              </thead>
              <tbody>
                {(manifest?.shots || []).slice(0, 200).map((s) => (
                  <tr key={s.shot_id} className="border-t border-white/5 hover:bg-white/5">
                    <td className="px-5 py-2 font-mono text-xs text-gold">{s.shot_id}</td>
                    <td className="px-5 py-2 font-mono text-xs text-slate-400 truncate max-w-md">{s.prefix}</td>
                    <td className="px-5 py-2 text-right tabular-nums">{fmt(s.frames_count)}</td>
                  </tr>
                ))}
                {(!manifest?.shots || manifest.shots.length === 0) && (
                  <tr><td colSpan={3} className="px-5 py-8 text-center text-slate-500">
                    Sin capturas todavía. Realiza disparos en el Shot Analyzer para alimentar el dataset.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          {manifest?.note && (
            <div className="px-5 py-3 text-xs text-slate-500 border-t border-gold/10 bg-navy/40">
              {manifest.note}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
